import { KeyboardArrowLeft, KeyboardArrowRight, NavigateNextRounded } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Breakpoint, Button, Container, MobileStepper, Stack } from '@mui/material';
import {
  Form,
  Formik,
  FormikConfig,
  FormikErrors,
  FormikHelpers,
  FormikProps,
  FormikValues,
} from 'formik';
import { Children, RefObject, cloneElement, isValidElement, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { StepProps, StepperNav } from 'components/forms';
import { useScroll, useWidth } from 'hooks';

// TODO: animate page transitions using react spring - https://codesandbox.io/s/cisbc
// https://www.youtube.com/watch?v=AR5Zbw2u_Ug
// docs: https://www.react-spring.dev/docs

// TODO: create application layout - hide footer on mobile (sits under stepper)

// TODO: use router instead of index state ??
// https://blog.logrocket.com/building-multi-step-wizards-with-formik-and-react-query/

// TODO: use react string transitions: https://www.react-spring.dev/docs/components/use-transition
// https://codesandbox.io/embed/physics-animation-in-react-router-5hn85?fontsize=14&hidenavigation=1&theme=dark

export interface FormikWizardProps<T = any> extends FormikConfig<T> {
  children: React.ReactElement<StepProps> | React.ReactElement<StepProps>[]; // JSX.Element | JSX.Element[];
  onCancel?: () => void;
  mutateOnSubmit?: (values: T, helpers: FormikHelpers<T>, initVals: T) => any;
  onMutateError?: (err: unknown) => void;
  // enableReinitialize?: boolean;
  // initialErrors?: FormikErrors<any>;
  loading?: boolean;
  submitButtonText?: string;
  disableNext?: boolean;
  initialIndex?: number;
  withStepper?: boolean;
  maxWidth?: Breakpoint | false;
  formRef?: RefObject<FormikProps<T>>;
}

export const FormikWizard = <T extends FormikValues>({
  children,
  initialValues,
  initialErrors = {},
  onSubmit,
  onCancel,
  mutateOnSubmit,
  onMutateError = (err) => toast.error('An error occurred'),
  loading = false,
  submitButtonText = 'Submit',
  disableNext = false,
  initialIndex = 0,
  withStepper = true,
  maxWidth = 'sm',
  formRef,
  ...rest
}: FormikWizardProps<T>) => {
  const [stepIndex, setStepIndex] = useState<number>(initialIndex);
  const [snapshot, setSnapshot] = useState(initialValues);
  const { isMobile } = useWidth();
  const { scrollToTop } = useScroll();

  const steps = useMemo(() => Children.toArray(children), [children]);

  const stepperNavProps = useMemo(
    () =>
      steps
        .filter((child) => isValidElement(child)) // @ts-ignore
        .map((step) => ({ navLabel: step.props?.stepperNavLabel || '' })),
    [steps]
  );

  let currentStep = useMemo(
    () => cloneElement(steps[stepIndex] as React.ReactElement),
    [steps, stepIndex]
  );
  const isLastStep = () => stepIndex === steps.length - 1;

  const next = (values: any, helpers?: FormikHelpers<any>) => {
    setSnapshot(values);
    setStepIndex(Math.min(stepIndex + 1, steps.length - 1));
    scrollToTop();

    setTimeout(() => {
      helpers?.validateForm(values);
    }, 100);
  };

  const previous = (values: any, validateForm: (values?: any) => Promise<FormikErrors<any>>) => {
    setSnapshot(values);
    setStepIndex(Math.max(stepIndex - 1, 0));
    scrollToTop();

    setTimeout(() => {
      validateForm(values);
    }, 100);
  };

  const handleSubmit = async (values: any, bag: FormikHelpers<any>) => {
    if (currentStep.props.onSubmit) {
      await currentStep.props.onSubmit(values, bag);
    }
    if (currentStep.props.mutateOnSubmit) {
      try {
        values = await currentStep.props.mutateOnSubmit(values, bag, snapshot);
        // console.log('MUTATED VALUES: ', values);
      } catch (err) {
        console.log('MUTATE ON SUBMIT ERROR: ', err);
        if (onMutateError) return onMutateError(err);
      }
    }
    if (isLastStep()) {
      return onSubmit(values, bag);
    } else {
      bag.setTouched({}); // TODO: remove ??

      next(values, bag);
    }
  };

  const handleCancel = (
    handleReset: (e?: React.SyntheticEvent<any, Event> | undefined) => void,
    setValues: FormikHelpers<T>['setValues']
  ) => {
    handleReset();
    console.log('RESETTING TO INITIAL VALUES: ', initialValues);
    setValues(initialValues, false);
    setStepIndex(0);
    if (onCancel) onCancel();
  };

  return (
    <Formik<T>
      initialValues={snapshot}
      initialErrors={currentStep?.props?.initialErrors || {}}
      validationSchema={currentStep.props.validationSchema}
      validateOnMount={true}
      onSubmit={handleSubmit}
      enableReinitialize
      innerRef={formRef}
      {...rest}
    >
      {({
        values,
        isValidating,
        isSubmitting,
        isValid,
        status,
        handleSubmit,
        validateForm,
        handleReset,
        setValues,
      }) => (
        <Form
          onSubmit={handleSubmit}
          autoComplete='off'
          style={{ width: '100%' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (isValidating || isSubmitting || !isValid) return;
              handleSubmit();
            }
          }}
        >
          {/* TODO: MOVE TO OUTSIDE COMPONENT ?? useContext? pass steps as props? */}
          {!isMobile ? (
            <>
              {withStepper && (
                // <Box sx={{ width: '100%', py: 5 }}>
                <Container
                  maxWidth={maxWidth}
                  disableGutters={true}
                  sx={{ py: 4, px: '0 !important' }}
                >
                  <StepperNav
                    activeStep={stepIndex}
                    labels={stepperNavProps}
                    setStep={(index: number) => setStepIndex(index)}
                  />
                </Container>
                // </Box>
              )}

              {currentStep}

              <Container maxWidth={maxWidth} disableGutters sx={{ px: '0 !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 4 }}>
                  {stepIndex > 0 && (
                    <Stack direction='row' spacing={2}>
                      <Button
                        onClick={() => previous(values, validateForm)}
                        disabled={isSubmitting}
                      >
                        Back
                      </Button>
                      <Button
                        variant='greyText'
                        onClick={() => handleCancel(handleReset, setValues)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  )}
                  <LoadingButton
                    type='submit'
                    loading={isSubmitting || isValidating || loading || status === 'loading'}
                    disabled={!isValid || disableNext}
                    loadingPosition='end'
                    endIcon={<NavigateNextRounded />}
                    sx={{ ml: 'auto' }}
                  >
                    {stepIndex === steps.length - 1 ? submitButtonText : 'Next'}
                  </LoadingButton>
                </Box>
              </Container>
            </>
          ) : (
            <Box>
              {currentStep}
              <MobileStepper
                variant='progress'
                steps={steps.length || 0}
                position='bottom'
                activeStep={stepIndex}
                sx={{ maxWidth: 480, flexGrow: 1, mt: 4, mx: 'auto' }}
                backButton={
                  <Button
                    size='small'
                    onClick={() => previous(values, validateForm)}
                    disabled={stepIndex === 0 || isValidating || isSubmitting}
                  >
                    {<KeyboardArrowLeft />}
                    Back
                  </Button>
                }
                nextButton={
                  <LoadingButton
                    type='submit'
                    size='small'
                    loading={isSubmitting || isValidating || loading || status === 'loading'}
                    disabled={!isValid || disableNext}
                    loadingPosition='end'
                    endIcon={<KeyboardArrowRight />}
                    sx={{ ml: 'auto' }}
                  >
                    {stepIndex === steps.length - 1 ? submitButtonText : 'Next'}
                  </LoadingButton>
                }
              />
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default FormikWizard;
